import {
	Component,
	ViewEncapsulation,
	HostBinding,
	Input,
	OnInit,
	OnDestroy,
	EventEmitter,
	NgZone
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
	ColDef,
	ICellRendererParams,
	ModuleRegistry,
	ViewportChangedEvent,
	GridReadyEvent,
	RowHeightParams,
	GridApi
} from '@ag-grid-community/core';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { Location } from '@angular/common';
import { Subscription, fromEvent, of, map } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';

import { ProductA2c, PRODUCT_TYPE_NF, PRODUCT_TYPE_F, PRODUCT_TYPE_FM, ORGANIC_COATED_CATEGORY,
	JOIN_STRING_FOR_CONCAT, AddToCartResponse } from '../../../models/offer';
import { AppWizardService } from '../../../services/app-wizard.service';
import { ConfigurationService } from '../../../../shared/services/configuration.api.service';
import { TRANSLATION_TYPE_SIDE } from '../../../../shared/models/data-with-translations';
import { ProductConfiguration } from '../../../../shared/models/product';
import { TranslationService } from '../../../../shared/services/translation.service';
import {
	NAMESPACE_RTPC_FIELD,
	ProductCharacteristicsTranslationService
} from '../../../../shared/services/product-characteristics-translation.services';
import { CommonColDef, IconColDef } from '../../../../shared/datacenter/column-defs';
import { CheckboxHeaderComponent } from '../../../../shared/datacenter/header-components/checkbox-header.component';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../shared/datacenter/grid-options';
import { formatTextDimension, parseNumberFromText } from '../../../../shared/utils/number.format';
import { DataSource } from '../../../../shared/datacenter/data-source';
import { ProductListColumnDefinition, DEFAULT_VIEW_PRODUCT_LIST, ORGANIC_COATED_VIEW_PRODUCT_LIST }
	from '../../../../shared/datacenter/types/product-list/product-list-configuration';
import { UserDatacenterView } from '../../../../shared/models/datacenter-aggrid-view';
import {
	DatacenterCodeTranslationFilterType,
	DatacenterDateNumericType, DocumentDateFilterType
} from '../../../../shared/models/datacenter-filters';
import { DATE_FORMAT_PATTERN_DD_MM_YYYY } from '../../../../shared/models/filter-formats';
import {
	ProductListViewService
} from '../../../../shared/components/view-customization/services/product-list-view.service';
import { createA2CProductCardUrl } from '../../cards/product-card/create-product-card-url';
import { ColDefWithFields } from '../../../../shared/datacenter/column-type';
import { ClickIconCellComponent } from '../../../../shared/datacenter/cell-renderers/click-icon-cell.component';
import { DialogWarningComponent } from '../../../../shared/components/dialog-warning/dialog-warning.component';
import { SubmitDialogComponent } from '../../../../shared/components/submit-dialog/submit-dialog.component';

import { AddToCartColDef } from './col-def/product-list-col-def';
import { ProductListHeaderComponent } from './header-def/product-list-header.component';

const resizeObservable = fromEvent(globalThis, 'resize');
@Component({
	selector: 'copo-offer-product-list',
	templateUrl: './product-list.component.html',
	encapsulation: ViewEncapsulation.Emulated,
	styleUrl: './product-list.component.css',
	imports: [
		MatButtonModule,
		TranslateModule,
		MatRippleModule,
		MatIconModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		AgGridModule,
	]
})
export class ProductListComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@Input() products!: ProductA2c[];
	@Input() soldTo!: string;
	@Input() subCategory!: string;
	@Input() form!: string;

	resizeSub?: Subscription;
	confirmationPopupSub: Subscription | undefined;
	configurationSub: Subscription | undefined;
	readonly formCtrlFilter = new FormControl('', { nonNullable: true });
	agGridModule = clientSideAgGridModules;
	columnDefs: ColDef[] = [];
	rowData!: ProductA2c[];
	note = '';
	currentSelectedView: UserDatacenterView;
	dataGridReady = new EventEmitter<GridApi>();
	productListColumnDef: ProductListColumnDefinition;
	currentColumns: string[] = [];
	dataSource?: DataSource;
	addToCartDialog: MatDialogRef<SubmitDialogComponent> | undefined;
	shipToValue = '';
	endUserValue = '';

	readonly gridOptions: CommonGridOptions<ProductA2c>;

	autoGroupColumnDef: ColDef = {
		headerName: '',
		cellRendererParams: {
			suppressCount: true,
			suppressEnterExpand: true,
			onClickAddToCart: (templateNumber: string, itemCount: number) => {
				this.addToCart(templateNumber, true, undefined, itemCount);
			}
		},
		colSpan(params) {
			if (params.data && !(<ProductA2c> params.data).emptyRow) {
				return 0;
			}
			return 50;
		},
		cellRenderer: ProductListHeaderComponent,
		tooltipValueGetter: () => '',
		width: 5
	};

	constructor(
		private readonly translationService: TranslationService,
		private readonly appWizardService: AppWizardService,
		readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private readonly viewService: ProductListViewService,
		private readonly configurationService: ConfigurationService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		readonly location: Location,
		readonly ngZone: NgZone,
		private readonly dialog: MatDialog,
		private readonly snackBar: MatSnackBar,
	) {
		this.gridOptions = new CommonGridOptions<ProductA2c>({
			translationService: this.translationService,
			suppressRowClickSelection: true,
			rowModelType: 'clientSide',
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			headerHeight: 70
		});
		ModuleRegistry.registerModules([RowGroupingModule]);
		this.currentSelectedView = DEFAULT_VIEW_PRODUCT_LIST;
		this.productListColumnDef = new ProductListColumnDefinition(
			translationService,
			productCharacteristicsTranslationService,
			NAMESPACE_RTPC_FIELD
		);
	}

	ngOnInit() {
		this.shipToValue = <string> this.appWizardService.getStateOfferForm().getFormShipTo().value;
		this.endUserValue = <string> this.appWizardService.getStateOfferForm().getFormEndUser().value;
		const templateIds: string[] = [];
		let nfcCount = 0;
		let productCount = 0;
		let fcCount = 0;
		const templateIdsHasFMPresence: string[] = [];
		const layoutKey = `EORDR_${ this.subCategory }_${ this.form }_layout`;
		this.currentSelectedView = this.getCurrentView(layoutKey, this.products[0].productCategoryCode);
		if (this.products.length > 0) {
			const emptyRow = this.translationService.getTR('eorder.product.empty.row');
			for (const product of this.products) {
				if (!templateIds.includes(product.customerTemplateNumber)) {
					templateIds.push(product.customerTemplateNumber);
				}
				product.dimension = formatTextDimension(
					product.thicknessValue ? Number(Number(product.thicknessValue).toFixed(2)) : '',
					product.widthValue ? Number(Number(product.widthValue).toFixed(0)) : '',
					product.lengthValue ? Number(Number(product.lengthValue).toFixed(0)) : '',
				);
				switch (product.productType) {
				case PRODUCT_TYPE_NF:
					this.updateNfcGroupHeaderDetailEmpty(product, emptyRow);
					nfcCount++;
					break;
				case PRODUCT_TYPE_F:
					fcCount++;
					break;
				case PRODUCT_TYPE_FM:
					if (!templateIdsHasFMPresence.includes(product.customerTemplateNumber)) {
						templateIdsHasFMPresence.push(product.customerTemplateNumber);
					}
					productCount++;
					break;
				}
			}
			this.products = this.products.filter(p => !(templateIdsHasFMPresence.includes(p.customerTemplateNumber)
				&& p.productType === PRODUCT_TYPE_NF));
			if (nfcCount > 0) {
				this.note = this.translationService.getTR('eorder.product.grid.note.nfc');
				this.note = this.note.replace('[X]', String(nfcCount));
				this.note = this.note.replace('[Y]', String(productCount + fcCount));
			} else if (fcCount > 0 && nfcCount === 0) {
				this.note = this.translationService.getTR('eorder.product.grid.note.fc');
				this.note = this.note.replace('[Y]', String(fcCount));
			}
			this.generateCodDefs(emptyRow);
		}
	}

	updateNfcGroupHeaderDetailEmpty(product: ProductA2c, emptyRow: string) {
		product.commercialSteelGradeIDDescription = '';
		product.lastUsed = '';
		product.userPartReference = '';
		product.commercialMetallicCoatingIDDescription = '';
		product.dimension = '';
		product.surfaceAspect = '';
		product.organicCoatingDataSheet = '';
		product.colourTop = '';
		product.removableFilm = '';
		product.organicCoatingDataSheetBottom = '';
		product.colorBottom = '';
		product.emptyRow = emptyRow;
	}

	ngOnDestroy(): void {
		if (this.resizeSub !== undefined) {
			this.resizeSub.unsubscribe();
		}
		if (this.confirmationPopupSub) {
			this.confirmationPopupSub.unsubscribe();
		}
		if (this.configurationSub) {
			this.configurationSub.unsubscribe();
		}
	}

	onGridReady(params: GridReadyEvent<ProductA2c>) {
		this.rowData = this.applyFilter();
		this.resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
	}

	getRowHeight(params: RowHeightParams): number | undefined | null {
		return params.node.group ? 42 : 32;
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	async goToCustomizeView() {
		const viewList = this.getViewList();
		this.viewService.viewToEdit = {
			currentView: this.currentSelectedView,
			viewType: this.currentSelectedView.type,
			viewSubType: `EORDR_${ this.subCategory }_${ this.form }_layout`,
			viewList
		};
		await this.router.navigate(
			['view'],
			{
				queryParams: { type: this.currentSelectedView.type },
				relativeTo: this.route,
				state:
					{
						headerName: `${
							this.appWizardService.getState().ui.getSelectedProductFamilyDescription()
						} ${
							this.translationService.getTR('datacenter.customize-view')
						}`,
						hideActionContainer: true,
						hideLayoutBlock: true,
						additionalInput: { soldTo: this.soldTo, subCategory: this.subCategory, form: this.form }
					}
			},
		);
	}

	generateCodDefs(emptyRow: string) {
		const fcToolTip = this.translationService.getTR('eorder.product.type.fc');
		const fmToolTip = this.translationService.getTR('eorder.product.type.fm');
		const addToCart = this.translationService.getTR('add.to.cart.label');
		const fcProductGroupLabel = this.translationService.getTR('eorder.product.fc.products.label');
		this.columnDefs = [
			new CommonColDef<ProductA2c>({
				sortable: false,
				valueGetter: params => {
					let response = '';
					if (params.data) {
						if (params.data.productType === PRODUCT_TYPE_F && params.data.productCategoryCode) {
							response = `${ fcProductGroupLabel }
							${ JOIN_STRING_FOR_CONCAT }${ PRODUCT_TYPE_F }${ JOIN_STRING_FOR_CONCAT }
							${ params.data.productCategoryCode }`;
						} else if (params.data.customerTemplateReference
							&& params.data.customerTemplateNumber && params.data.productCategoryCode) {
							response = `${ params.data.customerTemplateReference }
							${ JOIN_STRING_FOR_CONCAT }${ params.data.customerTemplateNumber }
							${ JOIN_STRING_FOR_CONCAT }${ params.data.productCategoryCode }`;
						}
					}
					return response;
				},
				rowGroup: true,
				hide: true,
				minWidth: 5,
				maxWidth: 5,
				width: 5
			}),
			new IconColDef<ProductA2c>({
				headerComponent: CheckboxHeaderComponent,
				headerName: this.translationService.getTR('guarantee.request.type'),
				cellStyle: { textAlign: 'center' },
				cellRendererParams: {
					icon(params: ICellRendererParams<ProductA2c, unknown>) {
						if (params.data) {
							switch (params.data.productType) {
							case PRODUCT_TYPE_F:
								return {
									iconName: 'fully-configured',
									toolTipText: fcToolTip
								};
							case PRODUCT_TYPE_FM:
								return {
									iconName: 'icon_from_mannequin',
									toolTipText: fmToolTip
								};
							}
							if (params.data.emptyRow === emptyRow) {
								return '';
							}
						}
						return {
							iconName: '',
							toolTipText: ''
						};
					},
					onClick() {
						// noop
					},
				},
				minWidth: 50,
				maxWidth: 50,
				width: 50
			}),
			new CommonColDef<ProductA2c>({
				headerComponent: CheckboxHeaderComponent,
				sortable: false,
				headerName: this.translationService.getTR('eorder.grid.header.view.label'),
				minWidth: 50,
				maxWidth: 50,
				width: 50,
				cellStyle: { textAlign: 'center' },
				cellRenderer: ClickIconCellComponent,
				cellRendererParams: {
					iconName: 'icon-eye',
					onIconClick: (params: ICellRendererParams<ProductA2c>) => {
						if (params.data) {
							window.open(createA2CProductCardUrl(params.data), '_blank');
						}
						return undefined;
					},
				},
				tooltipValueGetter: () => this.translationService.getTR('eoffer.product.card')
			}),
		];
		this.createColumnDefs();
		this.columnDefs.push(
			new AddToCartColDef<ProductA2c>({
				sortable: false,
				minWidth: 300,
				maxWidth: 300,
				width: 300,
				cellRendererParams: {
					inputParams: (params: ICellRendererParams<ProductA2c, unknown>) => {
						if (params.data && params.data.emptyRow !== emptyRow) {
							return {
								templateNumber: params.data.customerTemplateNumber,
								buttonLabel: addToCart,
								product: params.data
							};
						}
						return {};
					},
					onClickAddToCart: (product: ProductA2c, itemCount: number) => {
						this.addToCart(product.customerTemplateNumber, false, product.configurationNumber, itemCount);
					}
				}
			})
		);
	}

	private addToCart(
		templateNumber: string,
		isProductTypeNF: boolean,
		configurationNumber?: string,
		itemCount?: number
	) {
		try {
			if (itemCount) {
				if (this.soldTo) {
					if (isProductTypeNF) {
						const configurationData: ProductConfiguration = {
							productType: PRODUCT_TYPE_NF,
						};
						this.addToCartService(itemCount, templateNumber, configurationData);
					} else if (configurationNumber) {
						const configNumber = parseNumberFromText(configurationNumber);
						this.configurationSub = this.loadConfigurationData(String(configNumber))
							.subscribe({
								next: configurationData => {
									this.addToCartService(itemCount, templateNumber, configurationData);
								},
								error: (err: unknown) => {
									console.error('add to cart error :', err);
									if (this.addToCartDialog) {
										this.addToCartDialog.close();
									}
									this.openSnackBar(
										this.translationService.getTR('add.item.to.cart.error.msg')
									);
								},
								complete: () => {
									if (this.addToCartDialog) {
										this.addToCartDialog.close();
									}
								}
							});
					} else {
						console.error('configurationNumber not found');
						this.openSnackBar(this.translationService.getTR('global.error.unexpected'));
					}
				} else {
					this.dialog.open(DialogWarningComponent, {
						data: { iconName: 'alert',
							title: 'global.warning',
							content: this.translationService.getTR('eorder.add.item.empty.customer.warning'),
							cancelButtonEnabled: false }
					});
				}
			} else {
				console.error('item count to be added is empty');
				this.openSnackBar(this.translationService.getTR('global.error.unexpected'));
			}
		} catch (error) {
			console.error('Unexpected Error :', error);
			this.openSnackBar(this.translationService.getTR('global.error.unexpected'));
		}
	}

	private addToCartService(itemCount: number, templateNumber?: string, configData?: ProductConfiguration) {
		if (configData === undefined) {
			console.error('Unexpected Error: configuration load fails');
			this.openSnackBar(this.translationService.getTR('add.item.to.cart.error.msg'));
			if (this.addToCartDialog) {
				this.addToCartDialog.close();
			}
			return;
		}
		const addToCartResponse: AddToCartResponse = this.appWizardService.addToCart(
			configData,
			itemCount,
			templateNumber
		);
		if (this.addToCartDialog) {
			this.addToCartDialog.close();
		}
		if (!addToCartResponse.itemAdded) {
			if (addToCartResponse.messages.length > 0) {
				this.dialog.open(DialogWarningComponent, {
					data: {
						iconName: 'alert',
						title: 'global.warning',
						contents: addToCartResponse.messages,
						cancelButtonEnabled: false
					}
				});
			} else {
				console.error('Unexpected Error');
				this.openSnackBar(this.translationService.getTR('global.error.unexpected'));
			}
		} else {
			this.openSnackBar(this.translationService.getTR('eorder.add.item.success').replace(
				'[X]',
				String(itemCount)
			));
		}
	}

	private loadConfigurationData(configurationNumber: string) {
		if (this.appWizardService.getState().ui.hasConfigurationData(configurationNumber)) {
			return of(this.appWizardService.getState().ui.getConfigurationData(configurationNumber));
		}
		this.ngZone.run(() => {
			this.addToCartDialog = this.dialog.open<SubmitDialogComponent, undefined, number>(
				SubmitDialogComponent,
				{ panelClass: 'copo-offer-dialog', disableClose: true }
			);
			this.addToCartDialog.componentInstance.progressMessage =
				this.translationService.getTR('progress.message');
			this.addToCartDialog.componentInstance.waitMessage =
				this.translationService.getTR('wait.message.fastlane');
		});
		return this.configurationService.getMappedConfiguration(
			configurationNumber,
			TRANSLATION_TYPE_SIDE
		).pipe(
			map(configData => this.updateConfiguration(configurationNumber, configData))
		);
	}

	private openSnackBar(message: string) {
		this.snackBar.open(message, undefined, { duration: 5000 });
	}

	private updateConfiguration(configurationNumber: string, configuration: ProductConfiguration) {
		this.appWizardService.getState().ui.updateConfigurationData(configurationNumber, configuration);
		return configuration;
	}

	private getViewList() {
		return this.viewService.getViews();
	}

	private textFilter(value: string, listToCompare: string[]) {
		const lowerCaseValue = value.toLowerCase();
		for (const inputToCheck of listToCompare) {
			if (inputToCheck && lowerCaseValue.includes(inputToCheck.toLowerCase())) {
				return true;
			}
		}
		return false;
	}

	private numberFilter(value: number, numberRanges: DatacenterDateNumericType[]) {
		for (const numberRange of numberRanges) {
			if (value >= Number(numberRange.from) && value <= Number(numberRange.to)) {
				return true;
			}
		}
		return false;
	}

	private dateFilter(value: Date, dateRanges: DocumentDateFilterType[]) {
		for (const dateRange of dateRanges) {
			if (value >= dateRange.from && value <= dateRange.to) {
				return true;
			}
		}
		return false;
	}

	private applyFilter() {
		if (this.currentSelectedView.filterState) {
			const filters = this.currentSelectedView.filterState;
			return this.products.filter(product => filters.every(filter => {
				const fieldValue = product[filter.id];
				if (fieldValue && product.productType !== PRODUCT_TYPE_NF) {
					if (typeof filter.values[0] === 'string') {
						// text filter
						return this.textFilter(
							fieldValue,
							filter.values.map(String)
						);
					} else if ('code' in filter.values[0]) {
						// dropdown and suggestivelist filter
						return this.textFilter(
							fieldValue,
							filter.values.map(i => {
								const response = <DatacenterCodeTranslationFilterType> i;
								return response.code ?? '';
							})
						);
					} else if ('from' in filter.values[0]) {
						if (filter.id === 'lastUsed') {
							return this.dateFilter(
								moment(fieldValue, DATE_FORMAT_PATTERN_DD_MM_YYYY).toDate(),
								filter.values.map(i => <DocumentDateFilterType> i)
							);
						}
						return this.numberFilter(
							Number(fieldValue),
							filter.values.map(i => <DatacenterDateNumericType> i)
						);
					}
				}
				return false;
			}));
		}
		return this.products;
	}

	private getCurrentView(layoutKey: string, categoryCode?: string) {
		const allColumns = <ColDefWithFields[]> Array.from(
			this.productListColumnDef.PRODUCT_LIST_DEFAULT_CONFIGURATION().values()
		);
		const colIds: string[] = allColumns.map(item => item.colId ?? '').filter(colId => colId !== '');
		return this.viewService.getCurrentViewByKey(
			categoryCode === ORGANIC_COATED_CATEGORY ? ORGANIC_COATED_VIEW_PRODUCT_LIST : DEFAULT_VIEW_PRODUCT_LIST,
			'product-list',
			layoutKey,
			colIds
		);
	}

	private createColumnDefs() {
		let allColumns = <ColDefWithFields[]> Array.from(
			this.productListColumnDef.PRODUCT_LIST_DEFAULT_CONFIGURATION().values()
		);
		const groupIds = [
			...new Set(allColumns.filter(item => item.groupId !== undefined)
				.map(item => item.groupId ?? ''))
		];
		if (groupIds.length > 0) {
			allColumns = allColumns.filter(allcol => groupIds.length > 0
				&& !groupIds.includes(allcol.colId ?? ''));
		}

		let cols = this.viewService.convertViewToColumnDefinitions(this.currentSelectedView, allColumns);
		cols = cols.map(colDef => ({
			...colDef,
			width: colDef.width !== undefined ? colDef.width : undefined,
			minWidth: colDef.minWidth ?? 0,
			maxWidth: colDef.maxWidth !== undefined ? colDef.maxWidth : undefined,
			headerComponent: CheckboxHeaderComponent,
			cellRendererSelector: (params: ICellRendererParams<unknown>) =>
				!params.value
					? {
						component: undefined,
						params: undefined
					}
					: {
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						component: colDef.cellRenderer,
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						params: colDef.cellRendererParams
					}
		}));
		for (const col of cols) {
			this.columnDefs.push(col);
		}
	}
}
